/**
 * Order and wait (BLU-1506): the reads and writes behind the buyer's screens,
 * one module so the components stay presentational and a test mocks one thing.
 *
 *   useOrderAndWaitPos    the floor's building and sent POs (the To buy
 *                         cards) and its spares: ONE `list` call, scope pos.
 *   useOrderAndWaitPo     one PO's header, lines and orders (the PO page, the
 *                         Arrived preview): ONE `list` call, scope po. The
 *                         same input everywhere, so the page and the dialog
 *                         share one cache entry.
 *   useSendOrderAndWait   Send (building -> sent) and its toast.
 *   useArriveOrderAndWait Arrived (sent -> received, cost only) and its toast.
 *   useOrderAndWaitPlacement  Whether Place shows on this PO. A failed read reads as no.
 *   usePlaceOrderAndWait  Place: never retried, a second send could be a second order.
 *   useApiOrder           One PO's order with the supplier, polled while it goes out.
 *   useOpenApiOrders      The floor's unsettled supplier orders, for the To buy list.
 *   useApiOrderActions    What a person does with one afterwards. None is retried.
 *
 * Every read is the floor OWNER's: anyone else gets FORBIDDEN, never retried,
 * and the callers render it as "not yours", never as an error. The writes
 * resolve true or false; a failure has already raised its sticky toast in
 * plain words (`orderAndWaitErrorText`), so the caller only decides what stays
 * open.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ORDER_AND_WAIT_MAX_LIMIT, type SupplierApiOrderOpenRow } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import { isDefinitePlaceRefusal, orderAndWaitErrorText } from '@/features/supplier-catalog/lib/supplier-catalog-errors';

const SENDING_POLL_MS = 5_000;
const isSending = (status: string | null | undefined): boolean => status === 'queued' || status === 'posting';

/** A PO's orders read at once: the server's cap. More than this, and the Arrived preview counts blanks only. */
export const ORDER_AND_WAIT_PO_ORDERS_LIMIT = ORDER_AND_WAIT_MAX_LIMIT;

export function useOrderAndWaitPos(locationId: string | null) {
  const query = trpc.purchaseOrders.orderAndWait.list.useQuery(
    { locationId: locationId ?? '', scope: 'pos' },
    { enabled: Boolean(locationId), staleTime: 30_000 },
  );
  const data = query.data?.scope === 'pos' ? query.data : null;
  const forbidden = trpcErrorCode(query.error) === 'FORBIDDEN';
  return {
    purchaseOrders: data?.purchaseOrders ?? [],
    spares: data?.spares ?? [],
    loading: query.isLoading,
    forbidden,
    failed: Boolean(query.error) && !query.data && !forbidden,
    retrying: query.isFetching,
    retry: () => void query.refetch(),
  };
}

/** Orders and blanks waiting at the floor (distinct orders: one order can wait on two POs). */
export function useOrderAndWaitCount(locationId: string | null, enabled = true) {
  const active = enabled && Boolean(locationId);
  const query = trpc.purchaseOrders.orderAndWait.count.useQuery(
    { locationId: locationId ?? '' },
    { enabled: active, staleTime: 30_000 },
  );
  // A disabled query still hands back its cached answer; a floor that no longer asks has none.
  return active ? (query.data ?? null) : null;
}

export function useOrderAndWaitPo(locationId: string | null, poId: string | null, enabled = true) {
  const query = trpc.purchaseOrders.orderAndWait.list.useQuery(
    {
      locationId: locationId ?? '',
      scope: 'po',
      poId: poId ?? undefined,
      limit: ORDER_AND_WAIT_PO_ORDERS_LIMIT,
      offset: 0,
    },
    { enabled: enabled && Boolean(locationId) && Boolean(poId), staleTime: 30_000 },
  );
  const data = query.data?.scope === 'po' ? query.data : null;
  const code = trpcErrorCode(query.error);
  return {
    purchaseOrder: data?.purchaseOrder ?? null,
    orders: data?.orders ?? [],
    total: data?.total ?? 0,
    hasMore: data?.hasMore ?? false,
    /** True once the orders are on hand (the Arrived preview waits for them). */
    loaded: Boolean(data),
    loading: query.isLoading,
    forbidden: code === 'FORBIDDEN',
    notFound: code === 'NOT_FOUND',
    failed: Boolean(query.error) && !query.data && code !== 'FORBIDDEN' && code !== 'NOT_FOUND',
    retrying: query.isFetching,
    retry: () => void query.refetch(),
  };
}

/** What a write needs to know about the PO it acts on (ids plus the words for its toast). */
export interface OrderAndWaitPoRef {
  poId: string;
  poNumber: string;
  supplierName: string | null;
}

/**
 * After Send or Arrived: every Order and wait read, the PO page, the PO list and
 * the Order Queue's waiting view, plus the queue itself, its counts and the
 * batch scopes (a released order is batchable at once). The queue reads are not
 * awaited: the write does not wait on a heavy list it may not even be showing.
 */
function useInvalidateAfterWrite() {
  const utils = trpc.useUtils();
  return (poId: string) => {
    void utils.productionOrders.getOrderQueue.invalidate();
    void utils.productionOrders.getQueueCounts.invalidate();
    void utils.productionOrders.resolveBatchScopes.invalidate();
    return Promise.all([
      utils.purchaseOrders.orderAndWait.invalidate(),
      utils.purchaseOrders.getOrder.invalidate({ orderId: poId }),
      utils.purchaseOrders.getOrders.invalidate(),
      utils.productionOrders.getWaitingOnBlank.invalidate(),
    ]);
  };
}

export function useSendOrderAndWait() {
  const { t } = useTranslation('orders');
  const { t: tInventory } = useTranslation('inventory');
  const navigate = useNavigate();
  const invalidate = useInvalidateAfterWrite();
  const [pendingPoId, setPendingPoId] = useState<string | null>(null);
  // Returned so mutateAsync resolves onto fresh data (DESIGN.md section 12.2):
  // the card never flashes "Not sent yet" after Send.
  const mutation = trpc.purchaseOrders.orderAndWait.send.useMutation({
    onSuccess: (_result, { poId }) => invalidate(poId),
  });

  const send = async (po: OrderAndWaitPoRef, outSkuIds: string[]): Promise<boolean> => {
    if (pendingPoId) return false;
    setPendingPoId(po.poId);
    const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
    try {
      const result = await mutation.mutateAsync({ poId: po.poId, outSkuIds });
      const poNumber = result.poNumber ?? po.poNumber;
      if (result.alreadySent) {
        toast({ title: t('oaw.toast.alreadySent', { po: poNumber }) });
        return true;
      }
      const sentences = [t('oaw.toast.sentBody', { supplier })];
      if (result.outUnits > 0) {
        sentences.push(
          result.nextPurchaseOrder?.poNumber
            ? t('oaw.toast.sentOut', { count: result.outUnits, supplier, next: result.nextPurchaseOrder.poNumber })
            : t('oaw.toast.sentOutNoNumber', { count: result.outUnits, supplier }),
        );
      }
      if (result.droppedUnits > 0) sentences.push(t('oaw.toast.sentDropped', { count: result.droppedUnits }));
      toast({
        title: t('oaw.toast.sent', { po: poNumber }),
        description: sentences.join(' '),
        action: (
          <ToastAction altText={t('oaw.toast.open')} onClick={() => navigate(`/orders/${po.poId}`)}>
            {t('oaw.toast.open')}
          </ToastAction>
        ),
      });
      return true;
    } catch (error) {
      toast({
        title: t('oaw.toast.sendFailed'),
        description: orderAndWaitErrorText(tInventory, error, po.poNumber),
        variant: 'destructive',
      });
      return false;
    } finally {
      setPendingPoId(null);
    }
  };

  return { send, pendingPoId };
}

export function useArriveOrderAndWait() {
  const { t } = useTranslation('orders');
  const { t: tInventory } = useTranslation('inventory');
  const invalidate = useInvalidateAfterWrite();
  const [pendingPoId, setPendingPoId] = useState<string | null>(null);
  const mutation = trpc.purchaseOrders.orderAndWait.arrive.useMutation({
    onSuccess: (_result, { poId }) => invalidate(poId),
  });

  const arrive = async (po: OrderAndWaitPoRef, short: Array<{ skuId: string; arrived: number }>): Promise<boolean> => {
    if (pendingPoId) return false;
    setPendingPoId(po.poId);
    const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
    try {
      const result = await mutation.mutateAsync({ poId: po.poId, short });
      const poNumber = result.poNumber ?? po.poNumber;
      if (result.alreadyArrived) {
        toast({ title: t('oaw.toast.alreadyArrived', { po: poNumber }) });
        return true;
      }
      const sentences = [t('oaw.toast.arrivedReleased', { count: result.releasedOrderIds.length })];
      if (result.shortUnits > 0) {
        sentences.push(
          result.nextPurchaseOrder?.poNumber
            ? t('oaw.toast.arrivedShort', { count: result.shortUnits, supplier, next: result.nextPurchaseOrder.poNumber })
            : t('oaw.toast.arrivedShortNoNumber', { count: result.shortUnits, supplier }),
        );
      }
      if (result.stillWaitingOrderIds.length > 0) {
        sentences.push(t('oaw.toast.arrivedStillWaiting', { count: result.stillWaitingOrderIds.length }));
      }
      if (result.spareUnits > 0) sentences.push(t('oaw.toast.arrivedSpare', { count: result.spareUnits }));
      toast({ title: t('oaw.toast.arrived', { po: poNumber }), description: sentences.join(' ') });
      return true;
    } catch (error) {
      toast({
        title: t('oaw.toast.arriveFailed'),
        description: orderAndWaitErrorText(tInventory, error, po.poNumber),
        variant: 'destructive',
      });
      return false;
    } finally {
      setPendingPoId(null);
    }
  };

  return { arrive, pendingPoId };
}

/** Whether Place shows. A failed read reads as no: the person still has the website. */
export function useOrderAndWaitPlacement(poId: string | null, enabled = true) {
  const active = enabled && Boolean(poId);
  const query = trpc.purchaseOrders.orderAndWait.placement.useQuery(
    { poId: poId ?? '' },
    // Asked again on every open and while open: someone else's Place must reach a dialog left sitting.
    { enabled: active, retry: false, staleTime: 30_000, refetchOnMount: 'always', refetchInterval: 30_000 },
  );
  return {
    canPlace: query.data?.canPlace === true,
    moved: query.data?.stillDraft === false,
    shipTo: query.data?.shipTo ?? null,
    method: query.data?.method ?? null,
    loading: active && query.isLoading,
  };
}

export type PlaceOutcome =
  | { kind: 'done' }
  /** The server said no: nothing was claimed. */
  | { kind: 'failed' }
  /** No plain answer: the order may have gone out. */
  | { kind: 'unsure' }
  | { kind: 'lines_changed'; lines: Array<{ skuId: string; qty: number }> | null };

/** The words for a Place that found the order already claimed. Never "placed" for one that was refused or put back. */
function alreadyClaimedKey(apiOrderStatus: string): string {
  switch (apiOrderStatus) {
    case 'placed':
      return 'oaw.toast.alreadyPlaced';
    case 'queued':
    case 'posting':
      return 'oaw.toast.alreadyPlacing';
    case 'unknown':
      return 'oaw.toast.alreadyUnknown';
    case 'refused':
      return 'oaw.toast.alreadyRefused';
    case 'manual':
      return 'oaw.toast.alreadyWebsite';
    case 'put_back':
    case 'abandoned':
      return 'oaw.toast.alreadyPutBack';
    default:
      return 'oaw.toast.alreadyChanged';
  }
}

export function usePlaceOrderAndWait() {
  const { t } = useTranslation('orders');
  const { t: tInventory } = useTranslation('inventory');
  const navigate = useNavigate();
  const invalidate = useInvalidateAfterWrite();
  const [pendingPoId, setPendingPoId] = useState<string | null>(null);
  // retry false on purpose: a reply lost on the way back must not press Place twice.
  const mutation = trpc.purchaseOrders.orderAndWait.place.useMutation({ retry: false });

  const place = async (
    po: OrderAndWaitPoRef,
    outSkuIds: string[],
    expected: Array<{ skuId: string; qty: number }>,
  ): Promise<PlaceOutcome> => {
    if (pendingPoId) return { kind: 'failed' };
    setPendingPoId(po.poId);
    const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
    const openAction = (
      <ToastAction altText={t('oaw.toast.open')} onClick={() => navigate(`/orders/${po.poId}`)}>
        {t('oaw.toast.open')}
      </ToastAction>
    );
    try {
      const result = await mutation.mutateAsync({ poId: po.poId, outSkuIds, expected });
      await invalidate(po.poId);
      if (result.outcome === 'lines_changed') return { kind: 'lines_changed', lines: result.lines };
      const poNumber = result.poNumber ?? po.poNumber;
      if (result.alreadyClaimed) {
        toast({ title: t(alreadyClaimedKey(result.apiOrderStatus), { po: poNumber, supplier }), action: openAction });
        return { kind: 'done' };
      }
      const sentences = [t('oaw.toast.placingBody', { supplier })];
      if (result.outUnits > 0) {
        sentences.push(
          result.nextPurchaseOrder?.poNumber
            ? t('oaw.toast.sentOut', { count: result.outUnits, supplier, next: result.nextPurchaseOrder.poNumber })
            : t('oaw.toast.sentOutNoNumber', { count: result.outUnits, supplier }),
        );
      }
      if (result.droppedUnits > 0) sentences.push(t('oaw.toast.sentDropped', { count: result.droppedUnits }));
      toast({
        title: t('oaw.toast.placing', { po: poNumber, supplier }),
        description: sentences.join(' '),
        action: openAction,
      });
      return { kind: 'done' };
    } catch (error) {
      void invalidate(po.poId);
      if (!isDefinitePlaceRefusal(error)) {
        toast({
          title: t('oaw.toast.placeUnsure', { po: po.poNumber }),
          description: t('oaw.toast.placeUnsureBody', { supplier }),
          variant: 'destructive',
          action: openAction,
        });
        return { kind: 'unsure' };
      }
      toast({
        title: t('oaw.toast.placeFailed'),
        description: orderAndWaitErrorText(tInventory, error, po.poNumber, po.supplierName ?? undefined),
        variant: 'destructive',
      });
      return { kind: 'failed' };
    } finally {
      setPendingPoId(null);
    }
  };

  return { place, pendingPoId };
}

/** One PO's order with the supplier. Polled only while it is going out. */
export function useApiOrder(poId: string | null, enabled = true) {
  const query = trpc.purchaseOrders.orderAndWait.apiOrder.useQuery(
    { poId: poId ?? '' },
    {
      enabled: enabled && Boolean(poId),
      retry: false,
      staleTime: 30_000,
      refetchInterval: (current) => (isSending(current.state.data?.status) ? SENDING_POLL_MS : false),
    },
  );
  return { apiOrder: query.data ?? null };
}

/** The floor's unsettled supplier orders, by PO. One read on the small set; placed orders are not in it. */
export function useOpenApiOrders(locationId: string | null, enabled = true) {
  const query = trpc.purchaseOrders.orderAndWait.openApiOrders.useQuery(
    { locationId: locationId ?? '' },
    {
      enabled: enabled && Boolean(locationId),
      retry: false,
      staleTime: 30_000,
      refetchInterval: (current) => (current.state.data?.rows.some((row) => isSending(row.status)) ? SENDING_POLL_MS : false),
    },
  );
  const rows: readonly SupplierApiOrderOpenRow[] = query.data?.rows ?? [];
  return { rows };
}

export function useApiOrderActions(po: OrderAndWaitPoRef) {
  const { t } = useTranslation('orders');
  const { t: tInventory } = useTranslation('inventory');
  const invalidate = useInvalidateAfterWrite();
  const [pending, setPending] = useState<string | null>(null);
  const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
  const options = { retry: false } as const;
  const putBack = trpc.purchaseOrders.orderAndWait.putBack.useMutation(options);
  const notThere = trpc.purchaseOrders.orderAndWait.notThere.useMutation(options);
  const website = trpc.purchaseOrders.orderAndWait.website.useMutation(options);
  const found = trpc.purchaseOrders.orderAndWait.found.useMutation(options);
  const checkAgain = trpc.purchaseOrders.orderAndWait.checkAgain.useMutation(options);

  const run = async (action: string, call: () => Promise<unknown>, title: string): Promise<boolean> => {
    if (pending) return false;
    setPending(action);
    try {
      await call();
      await invalidate(po.poId);
      toast({ title });
      return true;
    } catch (error) {
      void invalidate(po.poId);
      toast({
        title: t('oaw.toast.actionFailed'),
        description: orderAndWaitErrorText(tInventory, error, po.poNumber, po.supplierName ?? undefined),
        variant: 'destructive',
      });
      return false;
    } finally {
      setPending(null);
    }
  };

  const { poId, poNumber } = po;
  return {
    pending,
    putBack: () => run('putBack', () => putBack.mutateAsync({ poId }), t('oaw.toast.putBack', { po: poNumber })),
    notThere: (typedPoNumber: string) =>
      run('notThere', () => notThere.mutateAsync({ poId, typedPoNumber }), t('oaw.toast.putBack', { po: poNumber })),
    website: () => run('website', () => website.mutateAsync({ poId }), t('oaw.toast.website', { po: poNumber })),
    found: (supplierOrderId: string) =>
      run('found', () => found.mutateAsync({ poId, supplierOrderId }), t('oaw.toast.found', { po: poNumber, supplier })),
    checkAgain: () => run('checkAgain', () => checkAgain.mutateAsync({ poId }), t('oaw.toast.checking', { supplier })),
  };
}
